/**
 * Handles the intersection changes for observed elements.
 * When an element enters the viewport, it becomes visible and its animation starts.
 * When an element leaves the viewport, it becomes hidden and its animation state is reset.
 * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects.
 * @param {IntersectionObserver} observer - The IntersectionObserver instance.
 */
const handleIntersection = (entries, observer) => {
	entries.forEach(entry => {
		const element = entry.target;
		const animationClass = element.dataset.animationClass; // Get animation class from data-animation-class
		const animationDelay = element.dataset.animationDelay; // Get animation delay from data-animation-delay

		if (entry.isIntersecting) {
			// Element entered the viewport
			element.style.visibility = 'visible'; // Make the element visible
			element.style.animationDelay = animationDelay; // Set the animation delay
			element.classList.add(animationClass, 'animated'); // Add animation classes (e.g., zoomIn and animated)
		} else {
			// Element left the viewport
			element.classList.remove(animationClass, 'animated'); // Remove animation classes
			element.style.animationDelay = ''; // Clear the animation delay
			element.style.visibility = 'hidden'; // Make the element hidden again

			// Force reflow/repaint to reset the animation for a potential re-trigger.
			// This is crucial for the animation to restart correctly when the element re-enters the viewport.
			void element.offsetWidth;
		}
	});
};

// Configuration options for the Intersection Observer.
const observerOptions = {
	root: null, // The viewport is used as the root (the element whose scroll area is used for observing targets).
	rootMargin: '0px', // No extra margin around the root.
	threshold: 0.15 // Percentage of the target element's visibility needed to trigger the callback (15% visible).
};

// Create a new Intersection Observer instance.
const observer = new IntersectionObserver(handleIntersection, observerOptions);

// Wait for the DOM to be fully loaded before querying elements.
document.addEventListener('DOMContentLoaded', () => {
	// Select all elements that have the 'data-animation-class' attribute.
	const animatableElements = document.querySelectorAll('[data-animation-class]');

	// Loop through each animatable element and observe it.
	animatableElements.forEach(element => {
		// At this point, all elements should already have visibility: hidden; in their inline style.
		observer.observe(element);
	});
});